import React from "react";
import { toast } from "sonner";
import { Twitter, Download, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Honest Share-to-X dialog.
// - canNative (mobile / Web Share API w/ files): ONE tap really shares the PNG file to X.
// - else (desktop): explicit 2-step — download the exact meme, then open the X composer (text only,
//   because X provides NO way to auto-attach an image from the browser). Never claims the image was shared.
export default function ShareToXDialog({
  open, onOpenChange, preview, canNative, blob, filename, text,
  onNativeShare, onManualInitiated, testidPrefix, title = "Share On X",
}) {
  const downloadMeme = () => {
    if (!blob) { toast.error("Couldn't prepare the image."); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Meme downloaded — attach it on X.");
  };
  const openX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
    onManualInitiated?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-md">
        <DialogHeader><DialogTitle className="font-display font-900 uppercase text-neon">{title}</DialogTitle></DialogHeader>
        {canNative ? (
          <DialogDescription className="font-mono text-[11px] text-dim -mt-1">
            This shares the actual meme image straight to X — tap Share, then pick X.
          </DialogDescription>
        ) : (
          <DialogDescription className="font-mono text-[11px] text-dim -mt-1">
            X's web composer can't attach images automatically. Download the meme first, open X, then attach the saved image.
          </DialogDescription>
        )}
        <div className="space-y-4 mt-2">
          {preview && (
            <div className="border-3 border-black bg-ink3 p-2" data-testid={`${testidPrefix}-preview`}>
              <img src={preview} alt="Meme preview" className="w-full max-h-72 object-contain" />
            </div>
          )}
          {canNative ? (
            <button onClick={onNativeShare} className="btn-neon w-full !text-base" data-testid={`${testidPrefix}-native`}>
              <Smartphone className="w-4 h-4" /> Share Image to X
            </button>
          ) : (
            <div className="space-y-2">
              <button onClick={downloadMeme} className="btn-neon w-full" data-testid={`${testidPrefix}-download`}>
                <Download className="w-4 h-4" /> 1. Download Meme
              </button>
              <button onClick={openX} className="btn-ghost w-full !border-gold hover:!bg-gold" data-testid={`${testidPrefix}-openx`}>
                <Twitter className="w-4 h-4" /> 2. Open X
              </button>
              <p className="font-mono text-[10px] text-dim text-center">
                On X, click the image icon and attach the meme you just downloaded.
              </p>
            </div>
          )}
          <button onClick={() => onOpenChange(false)} className="btn-ghost w-full !py-2 text-[11px]" data-testid={`${testidPrefix}-cancel`}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
